import React, { useContext, useRef } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { StyledLinkWrapper } from '@components/shared/SharedStyles';
import useDesktop from '@components/shared/useDesktop';
import { BREAKPOINT } from '@constants';
import { CartItem } from '@contracts';
import { getProductLink } from '@data/products';
import { ProductData } from '@data/storefront';
import { replaceText } from '@utils/handleTextReplacement';
import { useNavigateToProduct } from '@utils/useNavigateToProduct';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import useTrackedCartItem from '@utils/useTrackedCartItem';
import ProductShopButton from './ProductShopButton';
import ProductShopVideo from './ProductShopVideo';

interface Props {
  data: ProductData;
  textColor?: string;
  mobileShowDescription?: boolean;
  hideDescription?: boolean;
  desktopDisplay?: boolean;
  disableClickEvents?: boolean;
  noUppercaseTitle?: boolean;
  handleTracking?: (cartItem: CartItem) => void;
  callback?: () => void;
}

const ProductBox: React.FC<Props> = ({
  data,
  textColor,
  handleTracking,
  disableClickEvents,
  mobileShowDescription,
  hideDescription,
  desktopDisplay,
  noUppercaseTitle,
  callback = () => undefined
}) => {
  const ref = useRef();
  const location = useLocation();
  const { state } = useContext(Context);
  const { country } = state;
  const titleRef = useReplacedInnerHtml(data?.product_title?.text);
  const descriptionRef = useReplacedInnerHtml(data?.product_description?.text);
  const videoRef = useRef(null);
  const isDesktop = useDesktop(BREAKPOINT.laptop);
  const customLink = data.link?.text ?? '';
  const link = customLink.length > 0 ? customLink : getProductLink(data.product_code) ?? '';
  const showDescription = (isDesktop || mobileShowDescription) && !hideDescription;
  const { priceText, loaded, cartItem, boxConfig } = useTrackedCartItem({
    data,
    handleTracking
  });
  const { navigateToProduct } = useNavigateToProduct({ data, disableClickEvents, boxConfig, link, callback });

  const onVideoHover = () => {
    if (isDesktop && videoRef && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // hide the error. (video was removed from dom)
        });
      }
    }
  };

  return (
    <Container ref={ref} className="product-box-container" $textColor={textColor} data-testid="product-box">
      <ImageWrap onMouseOver={onVideoHover} className="has-link product-image-wrap">
        <OverlayStyledLinkWrapper to={link} onClick={(e: any) => navigateToProduct(e)} role="product-link" />
        {data.product_image ? (
          <img id="productImage" src={data.product_image?.url} alt={replaceText(data.product_image.alt, country, location) ?? ''} />
        ) : null}
        {isDesktop ? <ProductShopVideo videoUrl={data.product_video?.url} /> : null}
        {loaded ? (
          <ButtonOverlay className="button-overlay">
            <ProductShopButton data={data} cartItem={cartItem} link={link} navigateToProduct={navigateToProduct} />
          </ButtonOverlay>
        ) : null}
      </ImageWrap>
      <TextWrap data-testid="product-box-text" $desktopDisplay={desktopDisplay}>
        <Typography
          $variant={TextVariant.Heading7}
          $mobileVariant={desktopDisplay ? TextVariant.Heading7 : TextVariant.BodyBoldMD}
          ref={titleRef}
          to={link}
          onClick={(e: any) => navigateToProduct(e)}
          className="has-link"
          data-testid="product-box-title"
          $noUppercaseTitle={noUppercaseTitle}
        ></Typography>
        {showDescription && <Typography $variant={TextVariant.BodyRegularMD} ref={descriptionRef}></Typography>}
        {priceText && loaded && (
          <Typography $variant={TextVariant.BodyBoldMD} $mobileVariant={desktopDisplay ? TextVariant.BodyBoldMD : TextVariant.BodyBoldSM}>
            {priceText}
          </Typography>
        )}
      </TextWrap>
    </Container>
  );
};

export default ProductBox;

interface StyleProps {
  $textColor?: string;
}

const Container = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: 100%;
  width: 270px;
  flex-shrink: 0;
  color: ${(props) => props.$textColor};
`;

const ImageWrap = styled.div`
  background: ${Colors.ecru};
  margin-bottom: 16px;
  position: relative;
  height: 320px;
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 100%;
  border-radius: 12px;

  & img {
    z-index: 3;
    position: relative;
  }

  &:hover {
    cursor: pointer;
  }

  @media (min-width: ${BREAKPOINT.laptop}px) {
    &:hover {
      #productVideo {
        z-index: 4;
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .button-overlay {
        display: block;
      }
    }
  }

  @media (max-width: 768px) {
    height: auto;
  }
`;

const TextWrap = styled.div<{ $desktopDisplay?: boolean }>`
  max-width: 100%;
  color: inherit;
  text-align: left;
  display: flex;
  gap: 8px;
  flex-direction: column;

  @media (max-width: 768px) {
    gap: ${(props) => (props.$desktopDisplay ? '8px' : '2px')};
  }
`;

const OverlayStyledLinkWrapper = styled(StyledLinkWrapper)`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
`;

const ButtonOverlay = styled.div`
  position: absolute;
  z-index: 5;
  bottom: 0;
  left: 0;
  right: 0;
  display: none;
`;
